import type { GridTreeNode, ReorderValidationContext } from '@mui/x-data-grid-pro';

export const isLevelCompatible = (
  sourceLevel: number | undefined,
  targetLevel: number | undefined,
  sourceDepth: number,
  targetDepth: number,
): boolean =>
  sourceLevel !== undefined && targetLevel !== undefined
    ? sourceLevel === targetLevel
    : sourceDepth === targetDepth;

export type SiblingReorderContext = Pick<
  ReorderValidationContext,
  'sourceNode' | 'targetNode' | 'dropPosition' | 'prevNode' | 'nextNode'
>;

export const isNoOpReorder = (context: SiblingReorderContext): boolean => {
  if (context.sourceNode.id === context.targetNode.id) {
    return true;
  }
  if (context.dropPosition === 'above' && context.prevNode?.id === context.sourceNode.id) {
    return true;
  }
  if (context.dropPosition === 'below' && context.nextNode?.id === context.sourceNode.id) {
    return true;
  }
  return false;
};

type ReparentContext = {
  sourceNode: GridTreeNode;
  targetNode: GridTreeNode;
  sourceTypeLevelOrder?: number;
  targetTypeLevelOrder?: number;
};

export const validateParentReparent = ({
  sourceNode,
  targetNode,
  sourceTypeLevelOrder,
  targetTypeLevelOrder,
}: ReparentContext): boolean => {
  if (targetNode.id === sourceNode.parent) return false;
  if (sourceTypeLevelOrder !== undefined && targetTypeLevelOrder !== undefined) {
    return targetTypeLevelOrder === sourceTypeLevelOrder - 1;
  }
  // Depth fallback only works for non-root sources: root items (depth 0) need type data.
  if (sourceNode.depth > 0) {
    return targetNode.depth === sourceNode.depth - 1;
  }
  return false;
};

export const validateSiblingReorder = (context: SiblingReorderContext): boolean => {
  if (context.dropPosition === 'inside') {
    return false;
  }
  if (isNoOpReorder(context)) {
    return false;
  }
  return context.sourceNode.parent === context.targetNode.parent;
};
